'use client'
import { IFaqItem, IFaqTopicItem } from '@partment/types'
import { useQuery } from '@tanstack/react-query'
import { Entry, EntrySkeletonType } from 'contentful'
import { useLocale } from 'next-intl'
import { useMemo, useState } from 'react'

export const useHelpSearch = (
  data: Entry<EntrySkeletonType<IFaqTopicItem>, undefined, string>[],
) => {
  const locale = useLocale()
  const [searchTerm, setSearchTerm] = useState('')
  const [triggerSearch, setTriggerSearch] = useState(false)

  const queryKey = useMemo(
    () => ['help_links', 'search', searchTerm, locale],
    [searchTerm, locale],
  )

  const { data: results = [], isLoading: isLoadingSearch } = useQuery({
    queryKey,
    queryFn: async () => {
      if (!searchTerm.trim()) return []
      try {
        // Filter cached topics for matching FAQs
        const faqTopicMatches = data.filter((topic: any) => {
          const term = searchTerm.toLowerCase()

          // topic-level fields
          const title = topic.fields.title?.toLowerCase() ?? ''
          const description = topic.fields.description?.toLowerCase() ?? ''

          // match title or description?
          const topicMatches =
            title.includes(term) || description.includes(term)

          // FAQ-level fields
          const faqs = topic.fields.faqs as
            | EntrySkeletonType<IFaqItem>[]
            | undefined

          const faqMatches = faqs?.some((faq) => {
            const q = (faq.fields?.question ?? '').toLowerCase()
            const a = (faq.fields?.answer ?? '').toLowerCase()

            return q.includes(term) || a.includes(term)
          })

          return topicMatches || faqMatches
        })

        // Deduplicate
        const uniqueResults = new Map()
        for (const item of faqTopicMatches) {
          uniqueResults.set(item.sys.id, item)
        }

        return Array.from(uniqueResults.values())
      } catch (error) {
        console.error('Error searching FAQs:', error)
        return []
      }
    },
    enabled: triggerSearch && !!searchTerm.trim() && data.length > 0,
  })

  const search = (term: string) => {
    setSearchTerm(term)
    setTriggerSearch(true)
  }

  return {
    search, // call this on button click
    results,
    isLoading: isLoadingSearch,
  }
}
